/**
 * LA cart Subscribe & Save — turn a subscription on/off, or change its delivery
 * frequency, from inside the cart drawer (and the cart page).
 *
 * Seal's widget is PDP-only, so a pack built on /pages/build-a-pack reached checkout with
 * no way to subscribe. The plans are native Shopify selling plans, though, so the cart's
 * own `/cart/change` endpoint sets them — `selling_plan: <id>` to subscribe, `''` to go
 * back to one-time. No Seal JS involved.
 *
 * Three things this has to respect:
 *
 * 1. **Address the line by INDEX, never by key.** Shopify re-keys a line whenever its
 *    selling plan changes (verified live: the same variant went from `…:e175…` to
 *    `…:727d…`). Worse, `/cart/change` given a stale key answers 200 with the cart
 *    UNCHANGED — no error, nothing to catch — so a write aimed at a stale key silently
 *    evaporates and the checkbox ends up contradicting the cart.
 * 2. **One cart write at a time.** Two overlapping writes can land out of order, and the
 *    later response morphs the section last, so the UI settles on the older state. Every
 *    request is queued on a single chain and the controls are frozen while one is in
 *    flight; the queued request reads its line index at execution time, after the morph.
 * 3. **Horizon refreshes the cart by event, not by return value.** `cart-items-component`
 *    morphs itself from a `shopify:cart:lines-update` event whose `promise` resolves with
 *    pre-rendered section HTML; the free-shipping bar and cart bubble listen to the same
 *    event. We mirror that contract exactly, the way assets/la-cart-add.js does for adds.
 *
 * Listeners are delegated on `document` because the morph replaces the controls wholesale.
 */
(function () {
  if (window.LACartSubscribe) return;
  window.LACartSubscribe = true;

  /** Serializes cart writes — see note 2. */
  var queue = Promise.resolve();

  function changeUrl() {
    return (window.Theme && Theme.routes && Theme.routes.cart_change_url) || '/cart/change';
  }

  // Every cart section on the page, so the drawer and the cart page stay in step.
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function controlForLine(line) {
    return document.querySelector('[data-la-subs][data-line="' + line + '"]');
  }

  function setFrozen(frozen) {
    document.querySelectorAll('[data-la-subs]').forEach(function (root) {
      if (frozen) root.setAttribute('data-busy', '');
      else root.removeAttribute('data-busy');
    });
  }

  function setStatus(line, message) {
    var el = controlForLine(line) && controlForLine(line).querySelector('[data-la-subs-status]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  /**
   * Writes one line's selling plan and refreshes every cart section.
   * @param {string} line - 1-based cart line index.
   * @param {string} planId - Selling plan id, or '' for a one-time purchase.
   */
  function applyPlan(line, planId) {
    // Re-read the row now rather than closing over it: a morph may have replaced the
    // node between this request being queued and it running.
    var root = controlForLine(line);
    if (!root) return Promise.resolve();

    var quantity = parseInt(root.dataset.quantity, 10);
    if (!quantity) return Promise.resolve();

    setFrozen(true);
    setStatus(line, '');

    var sectionIds = cartSectionIds();
    var body = {
      line: Number(line),
      quantity: quantity,
      // '' removes the plan — confirmed against the live cart API; `null` is not
      // reliably honoured across Shopify's cart endpoints.
      selling_plan: planId || '',
    };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    var resolve;
    var reject;
    var promise = new Promise(function (res, rej) {
      resolve = res;
      reject = rej;
    });

    // Dispatched up front so the free-shipping bar can shimmer while the write is in
    // flight; every listener awaits this promise for the data.
    var event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
    event.action = 'update';
    event.promise = promise;
    document.dispatchEvent(event);

    return fetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || data.errors) throw new Error(data.description || data.message || 'Cart update failed');
          return data;
        });
      })
      .then(function (cart) {
        // cart-icon.js reads the Storefront-API camelCase shape; /cart/change returns the
        // AJAX cart, which only carries item_count. Same fix as la-cart-add.js.
        cart.totalQuantity = cart.item_count;
        resolve({
          cart: cart,
          detail: {
            items: cart.items,
            itemCount: cart.item_count,
            sections: cart.sections,
            didError: false,
            source: 'la-cart-subscribe',
          },
        });
        return cart;
      })
      .catch(function (error) {
        reject(error);
        console.warn('[la] subscription update failed', error);
        // The morph never ran, so the checkbox still shows what we optimistically
        // rendered. Put it back where the cart actually is.
        var current = controlForLine(line);
        var toggle = current && current.querySelector('[data-la-subs-toggle]');
        if (toggle) {
          toggle.checked = !toggle.checked;
          syncFrequencyVisibility(current);
        }
        setStatus(line, "Couldn't update the subscription. Please try again.");
      })
      .finally(function () {
        setFrozen(false);
      });
  }

  function syncFrequencyVisibility(root) {
    var toggle = root.querySelector('[data-la-subs-toggle]');
    var picker = root.querySelector('select[data-la-subs-plan]');
    if (toggle && picker) picker.hidden = !toggle.checked;
  }

  function planFor(root) {
    var picker = root.querySelector('[data-la-subs-plan]');
    return (picker && picker.value) || root.dataset.defaultPlan || '';
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var isToggle = target.matches('[data-la-subs-toggle]');
    var isPicker = target.matches('select[data-la-subs-plan]');
    if (!isToggle && !isPicker) return;

    var root = target.closest('[data-la-subs]');
    if (!root) return;

    var line = root.dataset.line;
    var planId;

    if (isToggle) {
      // Show the frequency picker immediately — waiting on the round trip reads as a
      // dead click on a control the shopper just enabled.
      syncFrequencyVisibility(root);
      planId = target.checked ? planFor(root) : '';
    } else {
      var toggle = root.querySelector('[data-la-subs-toggle]');
      if (toggle && !toggle.checked) return;
      planId = target.value;
    }

    queue = queue.then(function () {
      return applyPlan(line, planId);
    });
  });
})();
